import { Clock } from 'lucide-react';

import type { ExtractedPrazo } from '@/api/client';
import { Badge } from './badge';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

const UNIDADE_ABREV: Record<string, string> = {
  horas: 'h',
  dias: 'd',
  dias_uteis: 'd',
  meses: 'm',
  anos: 'a',
};

// "dias" e "dias_uteis" colapsam para "dias" de propósito: a unidade vem de
// extração por regex do texto e NÃO é uma determinação confiável da forma de
// contagem (CPC art. 219 — prazo processual conta-se em dias úteis mesmo sem a
// palavra "úteis"). O badge não afirma úteis/corridos; o tooltip remete ao tribunal.
const UNIDADE_EXTENSO: Record<string, string> = {
  horas: 'horas',
  dias: 'dias',
  dias_uteis: 'dias',
  meses: 'meses',
  anos: 'anos',
};

/**
 * Unidades em que um prazo para PRATICAR ATO é expresso. Uma menção em meses
 * ou anos no texto de uma intimação é quase sempre outra coisa — prescrição,
 * decadência, narrativa ("25 anos depois"). Porte do branch do Rubens.
 */
const UNIDADES_PARA_AGIR = new Set(['horas', 'dias', 'dias_uteis']);

/** Se esta menção é um prazo para praticar ato, e não uma referência a período. */
export function ehPrazoParaAgir(prazo: ExtractedPrazo | null | undefined): boolean {
  return !!prazo && UNIDADES_PARA_AGIR.has(prazo.unidade);
}

/** O primeiro prazo para AGIR, na ordem do texto — a prescrição citada antes
 *  não tapa o prazo de verdade. O lido por extenso só vale sem outro (como o
 *  `prazo_para_agir` do backend). */
export function primeiroPrazoParaAgir(prazos: ExtractedPrazo[] | null | undefined): ExtractedPrazo | null {
  const paraAgir = (prazos ?? []).filter(ehPrazoParaAgir);
  return paraAgir.find((p) => !p.por_extenso) ?? paraAgir[0] ?? null;
}

interface PrazoBadgeProps {
  prazos: ExtractedPrazo[] | null | undefined;
  /** "compact" mostra "15d", "full" mostra "15 dias". Default: "compact" */
  mode?: 'compact' | 'full';
}

/**
 * Badge indicando prazo mencionado em comunicação processual.
 *
 * Exibe apenas o primeiro prazo encontrado (o mais relevante por posição no texto).
 * Não aparece se prazos for vazio/null.
 */
export function PrazoBadge({ prazos, mode = 'compact' }: PrazoBadgeProps) {
  if (!prazos || prazos.length === 0) return null;

  const p = prazos[0];
  const label =
    mode === 'compact'
      ? `${p.dias}${UNIDADE_ABREV[p.unidade] ?? 'd'}`
      : `${p.dias} ${UNIDADE_EXTENSO[p.unidade] ?? 'dias'}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant="outline"
          className="cursor-help text-[11px] shrink-0 gap-1 text-muted-foreground"
        >
          <Clock className="h-3 w-3" />
          Prazo: {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p className="text-xs leading-relaxed">
          Prazo encontrado por leitura automática do texto. A forma de contagem (dias úteis ou
          corridos), a contagem e a data final do prazo são de sua responsabilidade — confira no
          tribunal.
        </p>
      </TooltipContent>
    </Tooltip>
  );
}
